import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import type { PreloadedQuery } from 'react-relay';
import { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	RouteResource,
	RouterContext,
	RouterDataContext,
} from '@atlassian/react-resource-router';
import QUERY_NEW, {
	type TopNavigationQuery,
	type TopNavigationQuery$variables,
} from '@jira/navigation-apps__atlassian-navigation/src/ui/__generated__/TopNavigationQuery.graphql';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import type { ApplicationPermissions } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { getProjectKeyOrId } from '@jira/router-resources__utils/src/getProjectKeyOrId.tsx';

const hasOnlyJSMAndCSMAccess = (appPermissions: ApplicationPermissions) => {
	const {
		hasServiceDeskAccess,
		hasCustomerServiceAccess,
		hasSoftwareAccess,
		hasProductDiscoveryAccess,
	} = appPermissions;

	return (
		hasServiceDeskAccess &&
		hasCustomerServiceAccess &&
		!hasSoftwareAccess &&
		!hasProductDiscoveryAccess
	);
};

/**
 * Derives the Relay operation variables the Jira top-navigation fragments need.
 *
 * Shared so the same derivation can feed both `topNav4UIStateResource` (the
 * standalone top-nav query) and the GlobalRoot query
 * (`useJiraAppChromeQuery`), which spreads `...TopNavigation_slotsQuery`
 * and (via `...AppChrome`) GTN's Notifications fragment. Those children
 * read these as root operation variables — including `collabContextRoutingAri`
 * for the unread-notification count.
 *
 * `forceAppSwitcherUnitProfile` is for GlobalRoot's GTN path only. That path
 * is gated by `navx-global-top-nav-jira` and fetches the unit profile required
 * by its platform-owned default AppSwitcher.
 */
export const getTopNavRelayVariables = (
	{ match, route }: RouterContext | RouterDataContext,
	{
		cloudId,
		isAnonymous,
		atlassianAccountId,
		appPermissions,
		appEditions,
	}: {
		cloudId: string;
		isAnonymous: boolean;
		atlassianAccountId: string | null;
		appPermissions: ApplicationPermissions;
		appEditions: { customerService: string };
	},
	{ forceAppSwitcherUnitProfile = false }: { forceAppSwitcherUnitProfile?: boolean } = {},
): TopNavigationQuery$variables => {
	const collabContextRoutingAri = fg('post-office_enable_routing_workspace_id_prop')
		? PlatformSiteAri.create({ siteId: cloudId }).toString()
		: undefined;
	const issueKey = match.params.issueKey ?? '';
	const projectKey = match.params.projectKey ?? '';
	const projectIdOrKey = getProjectKeyOrId(match);

	const isCsmRoute =
		match.path.startsWith('/jira/customer-service-management') ||
		match.path.startsWith('/jira/helpcenter') || // note: remove when cleaning csm_appification_standalone_navigation
		match.path.startsWith('/jira/customer-service'); // note: remove when cleaning csm_appification_standalone_navigation

	return {
		cloudId,
		isAnonymous,
		atlassianAccountId: atlassianAccountId ?? '',
		issueKey: issueKey ?? '',
		shouldCheckProjectTypeForIssue:
			route.group === 'issue' && Boolean(issueKey) && hasOnlyJSMAndCSMAccess(appPermissions),
		projectKey: projectKey ?? '',
		shouldCheckProjectType: Boolean(projectKey) && hasOnlyJSMAndCSMAccess(appPermissions),
		projectIdOrKey,
		shouldFetchProjectTypeForSettingsAndEA: !isAnonymous && Boolean(projectIdOrKey),
		// we can SSR csmLiveChatAgent status indicator
		shouldFetchCSMLiveChatAgent:
			appPermissions.hasCustomerServiceAccess &&
			appEditions.customerService === PREMIUM_EDITION &&
			isCsmRoute,
		// The aem_edition_awareness was elevated to this file from EditionAwarenss, I coudldn't preserve the original code path as isFedRamp calls are now errors at build time
		// And we can't add the 2 flags here, I configured my flag as a depedant on the aem_edition_awareness flag
		skipEditionAwarenessFragment:
			fg('ea_early_fedramp_jira_check') &&
			// fg('aem_edition_awareness') &&
			!isFeatureEnabled('jira', 'jira-edition-awareness'),
		// we can't SSR csmLiveChatAgent
		// let's get the project type to check if we should mount csmLiveChatAgent status indicator
		// note: remove when cleaning csm_appification_standalone_navigation
		shouldFetchProjectType:
			appPermissions.hasCustomerServiceAccess &&
			appEditions.customerService === PREMIUM_EDITION &&
			Boolean(projectIdOrKey) &&
			!isCsmRoute,
		// The project favicon (`...ProjectFaviconService_query`) reads the project
		// avatar off whichever top-nav query is in play, so it is only fetched on
		// routes that name a project — where `jira_projectByIdOrKey` is already being
		// fetched above, making the avatar one more field on a node in flight. Whether
		// the instance uses project icons as favicons is checked where the favicon is
		// applied, in `useFaviconService`.
		shouldFetchProjectFavicon: Boolean(projectIdOrKey),
		skipHelpButtonRelayRead: isAnonymous,
		collabContextRoutingAri,
		appSwitcherProductAri: `ari:cloud:confluence::site/${cloudId}`,
		// The standalone Nav4 app switcher does not render the unit-tile icon.
		// Only Global Top Nav enables this query through its own gate.
		skipAppSwitcherUnitProfile: !forceAppSwitcherUnitProfile,
	};
};

export const topNav4UIStateResource: RouteResource<
	PreloadedQuery<TopNavigationQuery, Record<string, unknown>>
> = createRelayResource<TopNavigationQuery>({
	type: 'RELAY_RESOURCE_TYPE_TOPNAV_UI_STATE_RELAY',
	// Suppressed under the Nav Platform code-path, where this query's data is
	// already on the app-chrome query (`useJiraAppChromeQuery` spreads
	// `...TopNavigation_slotsQuery` with the variables `getTopNavRelayVariables`
	// derives above) and `NavPlatformTopNav` reads it from there. The gate also
	// swaps in `NavPlatformLayout`, so `TopNavigation` -- this resource's only
	// consumer -- never renders: without this predicate the query is an
	// `isCritical`, SSR-blocking fetch whose result nothing reads, and its
	// `store-and-network` policy means it hits the network even once the
	// app-chrome query has populated the store (NAVX-5414).
	//
	// Gated here rather than at each declaration site: this resource is declared
	// by both `getNavigationResources` and `getCustomerServiceL1NavigationResources`,
	// and gating per-site means a new route family can reintroduce the duplicate
	// fetch just by declaring it. One choke point cannot be bypassed that way.
	//
	// No `| null` widening of the exported type is needed here (unlike
	// `navPlatformSidebarResource`): the sole consumer reads this through
	// `useRelayResource`, whose `queryReference` is already `| null`, and
	// `TopNavigation` returns null on a falsy reference. `useRelayResource`
	// accepts only a non-null `RouteResource<PreloadedQuery<TQuery>>`, so
	// widening would break that call site rather than inform it.
	skip: () => fg('navx-global-top-nav-jira'),
	getQuery: (routerContext, { tenantContext }) => {
		return {
			parameters: QUERY_NEW,
			variables: getTopNavRelayVariables(routerContext, tenantContext),
			options: { fetchPolicy: 'store-and-network' },
		};
	},
	isCritical: true,
});
